import { useCallback } from "react";
import { useCustomer } from "autumn-js/react";
import { useSetAtom } from "jotai";
import { id } from "@instantdb/admin";
import { upsertChat } from "instantdb/client/chat";
import { upsertMessage } from "instantdb/client/message";
import { useUser } from "@/hooks/useUser";
import { useChatTitle } from "@/hooks/useChatTitle";
import { useRouter } from "@tanstack/react-router";
import { firstAIMessageAtom, loadingChatIdsAtom } from "@/state/chatAtoms";
import { useAIUsageCheck } from "@/hooks/useAIUsageCheck";
import { authDialog } from "@/state/dialogs";
import { isAdvancedModel } from "@/lib/aiproviders/models";
import { MESSAGE_TYPES } from "@/config/constants";

export const useSubmitMessage = ({ chatId }: { chatId?: string }) => {
  const { userId, isAuthenticated, hasPro, refetchUser } = useUser();
  const { refetch: refetchCustomer } = useCustomer();
  const router = useRouter();
  const { updateChatTitle } = useChatTitle();
  const { checkUsage } = useAIUsageCheck();
  const setFirstAIMessage = useSetAtom(firstAIMessageAtom);
  const setAuthDialogState = useSetAtom(authDialog);
  const setLoadingChatIds = useSetAtom(loadingChatIdsAtom);

  const submitMessage = useCallback(
    async (data: { message: string; model: string }): Promise<boolean> => {
      const { message: content, model: selectedModel } = data;

      const isAdvanced = isAdvancedModel(selectedModel);

      if (!isAuthenticated) {
        // Show authDialog
        setAuthDialogState({ open: true });
        return false;
      }

      // Check if user has enough AI suggestions
      if (!checkUsage(selectedModel)) {
        // The checkUsage function will open the pro dialog if needed
        return false;
      }

      // Refetch user data after message is processed to update usage count
      // The server increments the count when processing the API request
      setTimeout(() => {
        refetchUser();
        refetchCustomer();
      }, 1000);

      // If no active chat, create a new one
      if (!chatId) {
        const newChatId = id();

        // Navigate to the new chat after saving the message
        router.navigate({
          to: "/chat/$chatId",
          params: { chatId: newChatId },
        });

        // Add chat to loading state
        setLoadingChatIds((prev) => new Set(prev).add(newChatId));

        // Create chat and save user message simultaneously
        const messageId = id();
        await Promise.all([
          upsertChat({
            id: newChatId,
            userId,
            title: "New Chat", // Temporary title
            isPrivate: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }),
          upsertMessage(
            {
              id: messageId,
              userId,
              content,
              role: "user",
              model: selectedModel,
              isAdvanced,
              hasPro,
              messageType: MESSAGE_TYPES.TEXT,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            newChatId,
          ),
        ]);

        // Generate AI title based on the first message
        updateChatTitle(newChatId, userId, content);

        // Store the first message for AI processing after navigation
        setFirstAIMessage({
          chatId: newChatId,
          content,
          model: selectedModel,
        });

        return true;
      }

      // Add chat to loading state
      setLoadingChatIds((prev) => new Set(prev).add(chatId));

      // Save user message with explicit ID
      const messageId = id();
      await upsertMessage(
        {
          id: messageId,
          userId,
          role: "user",
          content,
          model: selectedModel,
          isAdvanced,
          hasPro,
          messageType: MESSAGE_TYPES.TEXT,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        chatId,
      );

      return true;
    },
    [
      userId,
      router,
      updateChatTitle,
      chatId,
      setFirstAIMessage,
      checkUsage,
      refetchUser,
      setLoadingChatIds,
    ],
  );

  return { submitMessage };
};
